/// <reference types="trusted-types" />
import { z } from 'zod';
import DOMPurify from 'dompurify';
import {
    CloverEcommerceTokenizationProviderResponseSchema,
    CloverHostedFieldsBrowserTokenizationSessionSchema,
    CloverTokenizationTokenSchema,
} from '../../schemas/ipg/clover/clover-tokenization.schemas';
import type { CloverHostedFieldsBrowserTokenizationSession } from '../../types/ipg/clover/clover-tokenization.types';
import {
    CloverTokenizationResultType,
    type CloverEcommerceBrowserTokenizationResultV2,
} from '../../types/ipg/clover/clover-tokenization.types';

export const CloverHostedFieldType = {
    CARD_NUMBER: 'CARD_NUMBER',
    CARD_DATE: 'CARD_DATE',
    CARD_CVV: 'CARD_CVV',
    CARD_POSTAL_CODE: 'CARD_POSTAL_CODE',
} as const;

export interface CloverHostedFieldsMountTargets {
    readonly cardNumber: string;
    readonly cardDate: string;
    readonly cardCvv: string;
    readonly cardPostalCode?: string;
}

export interface CloverHostedFieldElement {
    mount(target: string): void;
}

export interface CloverHostedFieldElements {
    create(
        type: (typeof CloverHostedFieldType)[keyof typeof CloverHostedFieldType],
        styles: Readonly<Record<string, Readonly<Record<string, string>>>>
    ): CloverHostedFieldElement;
}

interface CloverHostedFieldsTokenResult {
    readonly token?: unknown;
    readonly errors?: unknown;
}

export interface CloverHostedFieldsSdk {
    elements(): CloverHostedFieldElements;
    createToken(): Promise<CloverHostedFieldsTokenResult>;
}

export interface CloverHostedFieldsConstructor {
    new (apiAccessKey: string, options: { readonly merchantId: string }): CloverHostedFieldsSdk;
}

declare global {
    interface Window {
        Clover?: CloverHostedFieldsConstructor;
    }
}

export interface CloverHostedFieldsController {
    createToken(): Promise<CloverEcommerceBrowserTokenizationResultV2>;
}

export interface CloverHostedFieldsDocument {
    readonly document: Document;
    dispose(): void;
}

// Default policies survive document.open(); ownership follows their factory's realm.
const weakMap = new WeakMap<TrustedTypePolicyFactory, string>();

// The SDK retains its first merchant, intermediary ID and anonymous listeners
// for a Window's lifetime. The exact owned browsing context is its teardown.
export async function initializeCloverHostedFieldsDocument(input: {
    readonly htmlIFrameElement: HTMLIFrameElement;
    readonly sdkUrl: string;
    readonly abortSignal: AbortSignal;
}): Promise<CloverHostedFieldsDocument> {
    const htmlIFrameElement = input.htmlIFrameElement;
    const parentDocument = htmlIFrameElement.ownerDocument;
    const document = htmlIFrameElement.contentDocument;
    const childWindow = document?.defaultView;
    const parentWindow = parentDocument.defaultView;
    if (
        document === null ||
        childWindow === undefined ||
        childWindow === null ||
        parentWindow === null
    ) {
        throw new Error('Unable to initialize Clover secure card entry.');
    }
    // open/close adopts the creator's URL/origin without an HTML-string sink.
    document.open();
    const htmlElement = document.createElement('html');
    htmlElement.append(document.createElement('head'), document.createElement('body'));
    document.append(htmlElement);
    await new Promise<void>((resolve, reject) => {
        const cleanup = (): void => {
            document.removeEventListener('DOMContentLoaded', ready);
            input.abortSignal.removeEventListener('abort', cancel);
        };
        const ready = (): void => {
            cleanup();
            resolve();
        };
        const cancel = (): void => {
            cleanup();
            reject(input.abortSignal.reason);
        };
        document.addEventListener('DOMContentLoaded', ready, { once: true });
        input.abortSignal.addEventListener('abort', cancel, { once: true });
        document.close();
        if (input.abortSignal.aborted) cancel();
    });
    input.abortSignal.throwIfAborted();
    const trustedTypePolicyFactory = childWindow.trustedTypes;
    if (trustedTypePolicyFactory !== undefined) {
        const registeredSdkUrl = weakMap.get(trustedTypePolicyFactory);
        if (registeredSdkUrl === undefined) {
            const domPurify = DOMPurify(childWindow);
            trustedTypePolicyFactory.createPolicy('default', {
                createHTML: (html) => domPurify.sanitize(html, { RETURN_TRUSTED_TYPE: false }),
                createScriptURL: (url) => {
                    if (url !== input.sdkUrl) {
                        throw new TypeError('Unapproved Clover script URL.');
                    }
                    return url;
                },
            });
            weakMap.set(trustedTypePolicyFactory, input.sdkUrl);
        } else if (registeredSdkUrl !== input.sdkUrl) {
            throw new TypeError('Unapproved Clover script URL.');
        }
    }
    const nonce = parentDocument.querySelector<HTMLScriptElement>('script[nonce]')?.nonce ?? '';
    const stylesheetLoads: Promise<void>[] = [];
    for (const parentHtmlLinkElement of parentDocument.querySelectorAll<HTMLLinkElement>(
        'link[rel="stylesheet"]'
    )) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = parentHtmlLinkElement.href;
        link.nonce = parentHtmlLinkElement.nonce;
        link.media = parentHtmlLinkElement.media;
        // A failed stylesheet still settles: the card box works unstyled.
        stylesheetLoads.push(
            new Promise<void>((resolve) => {
                link.addEventListener('load', () => resolve(), { once: true });
                link.addEventListener('error', () => resolve(), { once: true });
            })
        );
        document.head.append(link);
    }
    const style = document.createElement('style');
    style.nonce = nonce;
    // Clover styles its intermediary's wrapper with an inline style attribute,
    // which a strict style-src (the E-Invoice page) refuses; left in the flow,
    // its full-height frame grew the card box without bound (BUG-330). The
    // nonce'd sheet restates that style without hiding the intermediary.
    style.textContent = `body{margin:0}
            body>div:has(>#INTERMEDIATE_ID){top:0;position:fixed;height:1px;width:1px;pointer-events:none;opacity:0}
            .clover-footer{padding:18px 24px;background-color:#F4F5F5;border-radius:0 0 14px 14px;text-align:center}
            .clover-secure-payments{color:#B1B6B8;font-family:Roboto;font-size:14px;font-weight:bold;line-height:16px}
            .clover-privacy-link{font-family:Roboto;font-size:14px;font-weight:bold;line-height:16px}
            .clover-secure-payments img:first-of-type{margin:-2px 8px;max-width:12px}
            .clover-secure-payments img:last-of-type{margin:-4px 8px;max-width:20px}`;
    document.head.append(style);
    document.documentElement.className = parentDocument.documentElement.className;
    const parentStyle = parentWindow.getComputedStyle(htmlIFrameElement);
    for (const property of Array.from(parentStyle)) {
        if (property.startsWith('--')) {
            document.documentElement.style.setProperty(
                property,
                parentStyle.getPropertyValue(property)
            );
        }
    }
    document.body.style.color = parentStyle.color;
    document.body.style.font = parentStyle.font;
    // The owned document has no doctype, so it renders in quirks mode: its
    // body is never shorter than the frame, and a full-height field frame
    // resolves against the frame itself. Sizing the frame from
    // body.scrollHeight could therefore only grow, without bound, while
    // Clover's fields start full height (QA, 2026-10-30). The frame follows
    // the bottom of the in-flow content instead, and the fields mount only
    // after the page styles that give them their fixed height have loaded.
    const measureContentHeight = (): number => {
        let contentBottom = 0;
        for (const element of Array.from(document.body.children)) {
            const computedStyle = childWindow.getComputedStyle(element);
            if (computedStyle.position === 'fixed' || computedStyle.display === 'none') continue;
            contentBottom = Math.max(
                contentBottom,
                element.getBoundingClientRect().bottom +
                    (Number.parseFloat(computedStyle.marginBottom) || 0)
            );
        }
        return Math.ceil(contentBottom + childWindow.scrollY);
    };
    const resizeObserver = new ResizeObserver(() => {
        htmlIFrameElement.style.height = `${measureContentHeight()}px`;
    });
    const observeBodyChildren = (): void => {
        resizeObserver.observe(document.body);
        for (const element of Array.from(document.body.children)) resizeObserver.observe(element);
    };
    observeBodyChildren();
    const mutationObserver = new MutationObserver(observeBodyChildren);
    mutationObserver.observe(document.body, { childList: true });
    await new Promise<void>((resolve, reject) => {
        const cancel = (): void => reject(input.abortSignal.reason);
        input.abortSignal.addEventListener('abort', cancel, { once: true });
        Promise.all(stylesheetLoads).then(() => {
            input.abortSignal.removeEventListener('abort', cancel);
            resolve();
        }, reject);
    }).catch((error: unknown) => {
        mutationObserver.disconnect();
        resizeObserver.disconnect();
        throw error;
    });
    return {
        document,
        dispose: () => {
            mutationObserver.disconnect();
            resizeObserver.disconnect();
        },
    };
}

const CloverHostedFieldStyles = {
    body: {
        margin: '0',
        padding: '0',
        background: '#1c1c1e',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
    },
    input: {
        boxSizing: 'border-box',
        width: '100%',
        height: '38px',
        padding: '0',
        background: '#1c1c1e',
        border: '0',
        outline: 'none',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
        fontSize: '14px',
        fontWeight: '400',
        lineHeight: '20px',
        color: 'rgba(255,255,255,.95)',
    },
    'input::placeholder': {
        color: 'rgba(255,255,255,.35)',
    },
} as const;

function readCloverConstructor(document: Document): CloverHostedFieldsConstructor {
    const cloverConstructor = document.defaultView?.Clover;
    if (cloverConstructor === undefined) {
        throw new Error('Clover secure card-entry SDK did not initialize.');
    }
    z.function().parse(cloverConstructor);
    return cloverConstructor;
}

export async function loadCloverHostedFieldsSdk(document: Document, sdkUrl: string): Promise<void> {
    const existingScript = Array.from(
        document.querySelectorAll<HTMLScriptElement>('script[data-clover-hosted-fields-sdk]')
    ).find((script) => script.dataset.cloverHostedFieldsSdk === sdkUrl);
    if (existingScript?.dataset.loaded === 'true') {
        return;
    }

    await new Promise<void>((resolve, reject) => {
        const script = existingScript ?? document.createElement('script');
        const handleLoad = (): void => {
            script.dataset.loaded = 'true';
            resolve();
        };
        const handleError = (): void => {
            script.remove();
            reject(new Error('Unable to load Clover secure card entry.'));
        };
        script.addEventListener('load', handleLoad, { once: true });
        script.addEventListener('error', handleError, { once: true });
        if (existingScript === undefined) {
            script.src = sdkUrl;
            script.nonce =
                document.defaultView?.frameElement?.ownerDocument.querySelector<HTMLScriptElement>(
                    'script[nonce]'
                )?.nonce ?? '';
            script.async = true;
            script.dataset.cloverHostedFieldsSdk = sdkUrl;
            document.head.append(script);
        }
    });
    readCloverConstructor(document);
}

function formatCloverTokenizationErrors(errors: unknown): string {
    const errorsResult = z.record(z.string(), z.string().trim().min(1)).safeParse(errors);
    if (!errorsResult.success) {
        return 'Clover could not tokenize the card details.';
    }
    const messages = Object.values(errorsResult.data);
    return messages.length === 0
        ? 'Clover could not tokenize the card details.'
        : messages.join(' ');
}

export function mountCloverHostedFields(input: {
    readonly document: Document;
    readonly session: CloverHostedFieldsBrowserTokenizationSession;
    readonly targets: CloverHostedFieldsMountTargets;
}): CloverHostedFieldsController {
    const session = CloverHostedFieldsBrowserTokenizationSessionSchema.parse(input.session);
    const Clover = readCloverConstructor(input.document);
    const clover = new Clover(session.api_access_key, {
        merchantId: session.merchant_id,
    });
    const elements = clover.elements();
    const mountedElements = [
        elements.create(CloverHostedFieldType.CARD_NUMBER, CloverHostedFieldStyles),
        elements.create(CloverHostedFieldType.CARD_DATE, CloverHostedFieldStyles),
        elements.create(CloverHostedFieldType.CARD_CVV, CloverHostedFieldStyles),
    ];
    mountedElements[0]?.mount(input.targets.cardNumber);
    mountedElements[1]?.mount(input.targets.cardDate);
    mountedElements[2]?.mount(input.targets.cardCvv);
    if (input.targets.cardPostalCode !== undefined) {
        const cloverHostedFieldElement = elements.create(
            CloverHostedFieldType.CARD_POSTAL_CODE,
            CloverHostedFieldStyles
        );
        cloverHostedFieldElement.mount(input.targets.cardPostalCode);
    }

    return {
        createToken: async (): Promise<CloverEcommerceBrowserTokenizationResultV2> => {
            const result = await clover.createToken();
            if (result.errors !== undefined) {
                throw new Error(formatCloverTokenizationErrors(result.errors));
            }
            const tokenResult = CloverTokenizationTokenSchema.safeParse(result.token);
            if (!tokenResult.success) {
                throw new Error('Clover secure card entry returned no valid token.');
            }
            const providerResponse =
                CloverEcommerceTokenizationProviderResponseSchema.parse(result);
            return {
                result_type: CloverTokenizationResultType.ECOMMERCE_BROWSER_TOKENIZATION_V2,
                provider_response: providerResponse,
                token: tokenResult.data,
            };
        },
    };
}
